import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Settings,
  Mail,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  RefreshCw,
  Phone,
  Link as LinkIcon,
  User,
  Wrench,
  Shield,
} from 'lucide-react';

import {
  getSettings,
  saveSettings,
  testSmtpConnection,
  getRingCentralAuthUrl,
  getRingCentralStatus,
  disconnectRingCentral,
  RingCentralStatusResponse,
  getEXOCollectionSettings,
  saveEXOCollectionSettings,
  EXOCollectionSettingsData,
} from '../../services/api';

interface SettingsPageProps {
  tab?: 'user' | 'system' | 'security';
}

export default function SettingsPage({ tab = 'user' }: SettingsPageProps) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);

  // SMTP form state
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromAddress, setSmtpFromAddress] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [configured, setConfigured] = useState(false);

  // RingCentral state
  const [rcStatus, setRcStatus] = useState<RingCentralStatusResponse | null>(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcConnecting, setRcConnecting] = useState(false);
  const [rcDisconnecting, setRcDisconnecting] = useState(false);

  // EXO Collection state
  const [exoSettings, setExoSettings] = useState<EXOCollectionSettingsData | null>(null);
  const [exoLoading, setExoLoading] = useState(false);
  const [exoSaving, setExoSaving] = useState(false);

  // Load settings on mount and handle OAuth callback params
  useEffect(() => {
    loadSettings();
    loadRingCentralStatus();
    loadEXOSettings();

    // Check for RingCentral OAuth callback params
    const params = new URLSearchParams(location.search);
    const rcError = params.get('rc_error');
    const rcSuccess = params.get('rc_success');

    if (rcError) {
      setError(`RingCentral connection failed: ${rcError}`);
      // Clean up URL params
      window.history.replaceState({}, '', location.pathname);
    } else if (rcSuccess) {
      setSuccess('RingCentral connected successfully!');
      loadRingCentralStatus(); // Refresh status
      // Clean up URL params
      window.history.replaceState({}, '', location.pathname);
    }
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSettings();
      setConfigured(response.configured);
      if (response.smtp) {
        setSmtpHost(response.smtp.host);
        setSmtpPort(response.smtp.port);
        setSmtpUser(response.smtp.user);
        setSmtpPassword(response.smtp.password); // Will be masked
        setSmtpFromAddress(response.smtp.fromAddress);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    setTestResult(null);

    try {
      const request = {
        smtp: {
          host: smtpHost,
          port: smtpPort,
          user: smtpUser,
          password: passwordChanged ? smtpPassword : undefined,
          fromAddress: smtpFromAddress || smtpUser,
        },
      };

      const response = await saveSettings(request);
      if (response.success) {
        setSuccess('Settings saved successfully');
        setPasswordChanged(false);
        setConfigured(true);
        // Reload to get masked password
        await loadSettings();
      } else {
        setError('Failed to save settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      // If password was changed, test with new settings
      // Otherwise, test saved settings
      let result;
      if (passwordChanged && smtpPassword && !smtpPassword.startsWith('••••')) {
        result = await testSmtpConnection({
          host: smtpHost,
          port: smtpPort,
          user: smtpUser,
          password: smtpPassword,
          fromAddress: smtpFromAddress || smtpUser,
        });
      } else {
        result = await testSmtpConnection();
      }
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        error: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handlePasswordChange = (value: string) => {
    setSmtpPassword(value);
    setPasswordChanged(true);
  };

  // RingCentral functions
  const loadRingCentralStatus = async () => {
    setRcLoading(true);
    try {
      const status = await getRingCentralStatus();
      setRcStatus(status);
    } catch (err) {
      console.error('Failed to load RingCentral status:', err);
    } finally {
      setRcLoading(false);
    }
  };

  const handleConnectRingCentral = async () => {
    setRcConnecting(true);
    setError(null);

    // Detect iframe — RC OAuth page blocks iframe embedding (X-Frame-Options)
    // Pre-open a blank tab synchronously (in click handler) to avoid popup blockers
    let authWindow: Window | null = null;
    try {
      if (window.self !== window.top) {
        authWindow = window.open('', '_blank');
      }
    } catch {
      // Cross-origin iframe — definitely need new tab
      authWindow = window.open('', '_blank');
    }

    try {
      const result = await getRingCentralAuthUrl();
      if (result.success && result.authUrl) {
        if (authWindow) {
          // Navigate the pre-opened tab to RingCentral OAuth
          authWindow.location.href = result.authUrl;
          setSuccess('RingCentral authorization opened in a new tab. Complete it there, then refresh this page.');
        } else {
          // Not in iframe — navigate same page
          window.location.href = result.authUrl;
        }
      } else {
        if (authWindow) authWindow.close();
        const errMsg = result.error || 'Failed to get RingCentral auth URL';
        setError(errMsg);
      }
    } catch (err) {
      if (authWindow) authWindow.close();
      const errMsg = err instanceof Error ? err.message : 'Failed to connect to RingCentral';
      setError(errMsg);
    } finally {
      setRcConnecting(false);
    }
  };

  const handleDisconnectRingCentral = async () => {
    if (!confirm('Are you sure you want to disconnect RingCentral? You will no longer be able to send TAP codes via SMS.')) {
      return;
    }

    setRcDisconnecting(true);
    setError(null);
    try {
      const result = await disconnectRingCentral();
      if (result.success) {
        setSuccess('RingCentral disconnected successfully');
        setRcStatus({ success: true, connected: false });
      } else {
        setError(result.error || 'Failed to disconnect RingCentral');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect RingCentral');
    } finally {
      setRcDisconnecting(false);
    }
  };

  const loadEXOSettings = async () => {
    setExoLoading(true);
    try {
      const result = await getEXOCollectionSettings();
      if (result.success) {
        setExoSettings(result.settings);
      }
    } catch {
      // ignore
    }
    setExoLoading(false);
  };

  const handleSaveEXOSettings = async (updates: Partial<EXOCollectionSettingsData>) => {
    setExoSaving(true);
    try {
      const result = await saveEXOCollectionSettings(updates);
      if (result.success) {
        setExoSettings(result.settings);
        setSuccess('Security collection settings saved');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    }
    setExoSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'user' as const, name: 'My Settings', href: '/settings/user', icon: User },
    { id: 'system' as const, name: 'System Settings', href: '/settings/system', icon: Wrench },
    { id: 'security' as const, name: 'Security Collection', href: '/settings/security', icon: Shield },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            <p className="text-muted-foreground">
              {tab === 'user' ? 'Your personal integrations and preferences' : 'System-wide configuration'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((t) => (
            <Link
              key={t.id}
              to={t.href}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.name}
            </Link>
          ))}
        </nav>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <CheckCircle className="h-5 w-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* User Settings Tab - RingCentral */}
      {tab === 'user' && (
        <>
          {/* RingCentral Integration Card */}
          <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-muted">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h2 className="text-lg font-medium text-foreground">RingCentral Integration</h2>
                  <p className="text-sm text-muted-foreground">
                    Connect your RingCentral account to send TAP codes via SMS for enhanced security
                  </p>
                </div>
                {rcStatus?.connected && (
                  <span className="ml-auto px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                    Connected
                  </span>
                )}
                {rcStatus?.needsReconnect && (
                  <span className="ml-auto px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full">
                    Needs Reconnect
                  </span>
                )}
              </div>
            </div>

            <div className="p-6">
              {rcLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : rcStatus?.connected ? (
                <div className="space-y-4">
                  {/* Connected status */}
                  <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div className="flex-1">
                      <p className="font-medium text-green-800 dark:text-green-200">
                        RingCentral Connected
                      </p>
                      {rcStatus.extensionName && (
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Extension: {rcStatus.extensionName}
                        </p>
                      )}
                      {rcStatus.connectedAt && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Connected {new Date(rcStatus.connectedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {rcStatus.phoneNumber ? (
                      <div className="text-right">
                        <p className="text-xs text-green-600 dark:text-green-400">SMS From Number</p>
                        <p className="font-mono text-lg font-semibold text-green-800 dark:text-green-200">
                          {rcStatus.phoneNumber}
                        </p>
                      </div>
                    ) : (
                      <div className="text-right">
                        <p className="text-xs text-yellow-600 dark:text-yellow-400">No Phone Number</p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                          Reconnect to fix
                        </p>
                      </div>
                    )}
                  </div>

                  {rcStatus.needsReconnect && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Your RingCentral token has expired. Please reconnect to continue sending SMS.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleConnectRingCentral}
                      disabled={rcConnecting}
                      className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {rcConnecting ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Reconnecting...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Reconnect
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDisconnectRingCentral}
                      disabled={rcDisconnecting}
                      className="px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {rcDisconnecting ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Disconnecting...
                        </>
                      ) : (
                        'Disconnect'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Not connected */}
                  <div className="flex items-center gap-4 p-4 bg-muted border border-border rounded-lg">
                    <LinkIcon className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-foreground">
                        Not Connected
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Connect your RingCentral account to enable secure SMS delivery of TAP codes
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleConnectRingCentral}
                    disabled={rcConnecting}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {rcConnecting ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Phone className="h-4 w-4" />
                        Connect RingCentral
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RingCentral Info Box */}
          <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
              About RingCentral Integration
            </h3>
            <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1 list-disc list-inside">
              <li>TAP codes will be sent via SMS from your RingCentral phone number</li>
              <li>Instructions are sent via email, TAP code via SMS (split-channel security)</li>
              <li>Each technician should connect their own RingCentral account</li>
              <li>If RingCentral is not connected, TAP codes will be included in the email</li>
            </ul>
          </div>
        </>
      )}

      {/* System Settings Tab - SMTP */}
      {tab === 'system' && (
        <>
          {/* SMTP Settings Card */}
          <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-muted">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h2 className="text-lg font-medium text-foreground">Email Settings (SMTP)</h2>
                  <p className="text-sm text-muted-foreground">
                    Configure SMTP server for sending credential emails
                  </p>
                </div>
                {configured && (
                  <span className="ml-auto px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                    Configured
                  </span>
                )}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* SMTP Host */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    SMTP Server
                  </label>
                  <input
                    type="text"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.office365.com"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Port
                  </label>
                  <select
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={587}>587 (TLS/STARTTLS)</option>
                    <option value={465}>465 (SSL)</option>
                    <option value={25}>25 (Unencrypted)</option>
                  </select>
                </div>
              </div>

              {/* SMTP User */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Username / Email
                </label>
                <input
                  type="text"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  placeholder="notifications@umetech.net"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* SMTP Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Password / App Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={smtpPassword}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    placeholder={configured ? 'Enter new password to change' : 'Enter password'}
                    className="w-full px-3 py-2 pr-10 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {passwordChanged && (
                  <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                    Password will be updated when you save
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  For Office 365 with MFA, use an App Password
                </p>
              </div>

              {/* From Address */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  From Address
                </label>
                <input
                  type="text"
                  value={smtpFromAddress}
                  onChange={(e) => setSmtpFromAddress(e.target.value)}
                  placeholder="Same as username if blank"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-lg ${
                  testResult.success
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className={`flex items-center gap-2 ${
                    testResult.success
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    {testResult.success ? (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Connection successful!</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-5 w-5" />
                        <span className="font-medium">Connection failed: {testResult.error}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !smtpHost || !smtpUser}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {testing ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !smtpHost || !smtpUser || (!configured && !smtpPassword)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* SMTP Info Box */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              About SMTP Configuration
            </h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
              <li>Settings are securely stored in Azure Key Vault</li>
              <li>Passwords are encrypted and never displayed in full</li>
              <li>For Office 365 with MFA enabled, create an App Password in your Microsoft account security settings</li>
              <li>Test the connection before saving to ensure credentials are correct</li>
            </ul>
          </div>
        </>
      )}

      {/* Security Collection Tab */}
      {tab === 'security' && (
        <>
          {exoLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Nightly EXO Collection */}
              <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                <div className="px-6 py-4 border-b border-border bg-muted">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <h2 className="text-lg font-medium text-foreground">Nightly EXO Collection</h2>
                      <p className="text-sm text-muted-foreground">
                        Runs Exchange Online data collection for all clients during the nightly window
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Enable Nightly Collection</p>
                      <p className="text-xs text-muted-foreground">Collects auth audit, rule changes, message trace, SP/OneDrive, and password/MFA events</p>
                    </div>
                    <button
                      onClick={() => handleSaveEXOSettings({ nightlyEnabled: !exoSettings?.nightlyEnabled })}
                      disabled={exoSaving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        exoSettings?.nightlyEnabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        exoSettings?.nightlyEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Nightly Window Config */}
                  <div className="grid grid-cols-3 gap-4 pt-3 border-t border-border">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Start Hour</label>
                      <select
                        value={exoSettings?.nightlyWindowStart ?? 19}
                        onChange={(e) => handleSaveEXOSettings({ nightlyWindowStart: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">End Hour</label>
                      <select
                        value={exoSettings?.nightlyWindowEnd ?? 4}
                        onChange={(e) => handleSaveEXOSettings({ nightlyWindowEnd: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Timezone</label>
                      <select
                        value={exoSettings?.nightlyTimezone ?? 'America/Los_Angeles'}
                        onChange={(e) => handleSaveEXOSettings({ nightlyTimezone: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm"
                      >
                        <option value="America/Los_Angeles">Pacific</option>
                        <option value="America/Denver">Mountain</option>
                        <option value="America/Chicago">Central</option>
                        <option value="America/New_York">Eastern</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compromise Auto-Trigger */}
              <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                <div className="px-6 py-4 border-b border-border bg-muted">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                    <div>
                      <h2 className="text-lg font-medium text-foreground">Compromise Auto-Trigger</h2>
                      <p className="text-sm text-muted-foreground">
                        When the 2-hour scan detects compromise indicators, immediately triggers EXO collection
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Enable Compromise Trigger</p>
                      <p className="text-xs text-muted-foreground">Auto-collects EXO data when foreign sign-ins, attacker emails, or rule manipulation detected</p>
                    </div>
                    <button
                      onClick={() => handleSaveEXOSettings({ compromiseTriggerEnabled: !exoSettings?.compromiseTriggerEnabled })}
                      disabled={exoSaving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        exoSettings?.compromiseTriggerEnabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        exoSettings?.compromiseTriggerEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {exoSettings?.compromiseTriggerEnabled && (
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                        <strong>Note:</strong> Keep this off until AI analysis is tuned to reduce false positives.
                        Each trigger starts an Azure Automation runbook (~5 min/client).
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Last Modified */}
              {exoSettings?.modifiedAt && (
                <p className="text-xs text-muted-foreground">
                  Last modified: {new Date(exoSettings.modifiedAt).toLocaleString()} by {exoSettings.modifiedBy}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
